import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormField } from '@/components/molecules/FormField';
import { Button } from '@/components/atoms/Button';
import { ApiError } from '@/services/api/client';
import { authAPI } from '@/services/api/auth.api';
import {
  createChangePasswordSchema,
  type ChangePasswordFormData,
} from '@/utils/validation/auth.schemas';
import { cn } from '@/utils/cn';
import type { ChangePasswordFormProps, ChangePasswordLabels } from './ChangePasswordForm.types';
import styles from './ChangePasswordForm.module.scss';

const DEFAULT_LABELS: ChangePasswordLabels = {
  title: 'Change password',
  currentLabel: 'Current password',
  currentPlaceholder: 'Enter your current password',
  newLabel: 'New password',
  newPlaceholder: 'Enter your new password',
  newHelper: 'At least 8 characters, with upper, lower and a number.',
  confirmLabel: 'Confirm new password',
  confirmPlaceholder: 'Repeat your new password',
  submit: 'Update password',
  success: 'Your password has been updated.',
  currentIncorrect: 'The current password is incorrect.',
  genericError: 'Could not update the password. Please try again.',
  validation: {
    currentRequired: 'Enter your current password.',
    newMin: 'Must be at least 8 characters.',
    newUpper: 'Must include an uppercase letter.',
    newLower: 'Must include a lowercase letter.',
    newNumber: 'Must include a number.',
    confirmRequired: 'Confirm your new password.',
    mismatch: 'Passwords do not match.',
    sameAsCurrent: 'The new password must be different from the current one.',
  },
};

/** Etiquetas en español, para áreas sin i18n (p. ej. backoffice). */
export const CHANGE_PASSWORD_LABELS_ES: ChangePasswordLabels = {
  title: 'Cambiar contraseña',
  currentLabel: 'Contraseña actual',
  currentPlaceholder: 'Introduce tu contraseña actual',
  newLabel: 'Nueva contraseña',
  newPlaceholder: 'Introduce tu nueva contraseña',
  newHelper: 'Mínimo 8 caracteres, con mayúscula, minúscula y un número.',
  confirmLabel: 'Confirmar nueva contraseña',
  confirmPlaceholder: 'Repite tu nueva contraseña',
  submit: 'Actualizar contraseña',
  success: 'Tu contraseña se ha actualizado.',
  currentIncorrect: 'La contraseña actual no es correcta.',
  genericError: 'No se pudo actualizar la contraseña. Inténtalo de nuevo.',
  validation: {
    currentRequired: 'Introduce tu contraseña actual.',
    newMin: 'Debe tener al menos 8 caracteres.',
    newUpper: 'Debe incluir una mayúscula.',
    newLower: 'Debe incluir una minúscula.',
    newNumber: 'Debe incluir un número.',
    confirmRequired: 'Confirma tu nueva contraseña.',
    mismatch: 'Las contraseñas no coinciden.',
    sameAsCurrent: 'La nueva contraseña debe ser distinta de la actual.',
  },
};

export function ChangePasswordForm({ labels, className }: ChangePasswordFormProps) {
  const l = labels ?? DEFAULT_LABELS;
  const schema = useMemo(() => createChangePasswordSchema(l.validation), [l.validation]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ChangePasswordFormData>({ resolver: zodResolver(schema) });

  const [serverError, setServerError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const onSubmit = async (data: ChangePasswordFormData) => {
    setServerError(null);
    setSuccess(false);
    try {
      await authAPI.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      setSuccess(true);
      reset();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError(l.currentIncorrect);
      } else {
        setServerError(l.genericError);
      }
    }
  };

  return (
    <form className={cn(styles.form, className)} onSubmit={handleSubmit(onSubmit)} noValidate>
      <FormField
        label={l.currentLabel}
        type="password"
        placeholder={l.currentPlaceholder}
        autoComplete="current-password"
        error={errors.currentPassword?.message}
        {...register('currentPassword')}
      />

      <FormField
        label={l.newLabel}
        type="password"
        placeholder={l.newPlaceholder}
        helperText={l.newHelper}
        autoComplete="new-password"
        error={errors.newPassword?.message}
        {...register('newPassword')}
      />

      <FormField
        label={l.confirmLabel}
        type="password"
        placeholder={l.confirmPlaceholder}
        autoComplete="new-password"
        error={errors.confirmPassword?.message}
        {...register('confirmPassword')}
      />

      {serverError && (
        <p className={styles.form__error} role="alert">
          {serverError}
        </p>
      )}
      {success && (
        <p className={styles.form__success} role="status">
          {l.success}
        </p>
      )}

      <div className={styles.form__actions}>
        <Button type="submit" variant="primary" isLoading={isSubmitting}>
          {l.submit}
        </Button>
      </div>
    </form>
  );
}

export default ChangePasswordForm;
